import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import {
  Container, Typography, Box, Alert, Card, CardContent, Button, TextField, CircularProgress, Stack, Chip, Grid, Paper
} from '@mui/material';
import {
  AccountBalanceWallet as WalletIcon,
  Refresh as RefreshIcon,
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  Logout as LogoutIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { ThemeProvider } from '@mui/material/styles';
import { useWallet } from '../context/WalletContext';
import { useAdmin } from '../context/AdminContext';
import { NETWORKS, getPlatformFeeBalance, transferPlatformFees, getPlatformFeeWallet } from '../utils/contractUtils';
import { themeCustom, GradientPaper, HeaderBar } from '../utils/themeUtils';
import AdminLogin from '../components/AdminLogin';

export default function PlatformFees() {
  const {
    provider,
    signer,
    address,
    isConnected,
    currentNetwork,
    networkConfig
  } = useWallet();

  const { isAuthenticated, isLoading, logout } = useAdmin();

  const [feeBalances, setFeeBalances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [transferring, setTransferring] = useState({});
  const [alert, setAlert] = useState({ type: 'info', message: '' });
  const [showAlert, setShowAlert] = useState(false);
  const [transferAddress, setTransferAddress] = useState('');
  const [transferAmount, setTransferAmount] = useState('');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Popular tokens to check for fees
  const tokensToCheck = [
    { symbol: 'USDT', address: currentNetwork === 'ethereum' ? '0xdAC17F958D2ee523a2206206994597C13D831ec7' : '0x55d398326f99059ff775485246999027b3197955' },
    { symbol: 'USDC', address: currentNetwork === 'ethereum' ? '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48' : '0x8AC76a51Cc950d9822D68b83fE1Ad97Ed32cDmo' },
    { symbol: 'BNB', address: '0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c' },
    { symbol: 'ETH', address: '0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2' }
  ];

  // Fetch platform fee balances
  const fetchFeeBalances = async () => {
    if (!provider || !isConnected) return;

    setLoading(true);
    try {
      const balances = [];
      
      for (const token of tokensToCheck) {
        try {
          const balance = await getPlatformFeeBalance(token.address, currentNetwork, provider);
          if (parseFloat(balance.formattedBalance) > 0) {
            balances.push({
              ...token,
              ...balance
            });
          }
        } catch (error) {
          console.error(`Error fetching balance for ${token.symbol}:`, error);
        }
      }
      
      setFeeBalances(balances);
    } catch (error) {
      console.error('Error fetching fee balances:', error);
      setAlert({ type: 'error', message: 'Failed to fetch platform fee balances' });
      setShowAlert(true);
    } finally {
      setLoading(false);
    }
  };

  // Transfer fees to another address
  const transferFees = async (tokenAddress, amount) => {
    if (!signer || !transferAddress) {
      setAlert({ type: 'error', message: 'Please enter a valid address and amount' });
      setShowAlert(true);
      return;
    }

    try {
      setTransferring(prev => ({ ...prev, [tokenAddress]: true }));
      
      const token = feeBalances.find(t => t.address === tokenAddress);
      const amountInWei = ethers.utils.parseUnits(amount, token.decimals);
      
      const tx = await transferPlatformFees(tokenAddress, transferAddress, amountInWei, currentNetwork, signer);
      await tx.wait();
      
      setAlert({ type: 'success', message: `Successfully transferred ${amount} ${token.symbol} to ${transferAddress}` });
      setShowAlert(true);
      
      // Refresh balances
      await fetchFeeBalances();
    } catch (error) {
      console.error('Error transferring fees:', error);
      setAlert({ type: 'error', message: `Transfer failed: ${error.message}` });
      setShowAlert(true);
    } finally {
      setTransferring(prev => ({ ...prev, [tokenAddress]: false }));
    }
  };

  useEffect(() => {
    if (isConnected && provider) {
      fetchFeeBalances();
    }
  }, [isConnected, provider, currentNetwork]);

  // Alert animation
  useEffect(() => {
    if (alert.message) setShowAlert(true);
    if (alert.message && alert.type === 'success') {
      const timer = setTimeout(() => setShowAlert(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [alert]);

  // Show login if not authenticated
  if (isLoading) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        background: 'linear-gradient(135deg, #000000 0%, #141414 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <CircularProgress size={48} sx={{ color: '#0071EB' }} />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <AdminLogin />;
  }

  return (
    <ThemeProvider theme={themeCustom}>
      <Box sx={{ 
        minHeight: '100vh', 
        background: 'linear-gradient(135deg, #000000 0%, #141414 100%)',
        py: isMobile ? 1 : 6
      }}>
        <Container maxWidth="lg" sx={{ px: isMobile ? 0.5 : 2 }}>
          <HeaderBar>
            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
              <WalletIcon sx={{ 
                fontSize: isMobile ? 32 : 64, 
                mr: isMobile ? 1 : 2,
                filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.3))'
              }} />
              <Typography 
                variant={isMobile ? 'h6' : 'h4'} 
                fontWeight={700} 
                letterSpacing={1} 
                sx={{ 
                  color: '#fff', 
                  textShadow: '0 2px 8px rgba(0,0,0,0.3)',
                  background: 'linear-gradient(90deg, #ffffff 0%, #f5f5f1 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}
              >
                Platform Fee Management
              </Typography>
            </Box>
            <Button
              onClick={logout}
              startIcon={<LogoutIcon />}
              sx={{
                color: '#fff',
                border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: 2,
                px: 2,
                py: 1,
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.1)',
                  borderColor: 'rgba(255,255,255,0.5)'
                }
              }}
            >
              Logout
            </Button>
          </HeaderBar>

          <Stack spacing={isMobile ? 2 : 3}>
            {showAlert && alert && (
              <Alert severity={alert.type || 'info'}>{alert.message}</Alert>
            )}

            {/* Platform Wallet Info */}
            <GradientPaper>
              <Typography 
                variant={isMobile ? 'body1' : 'h6'} 
                gutterBottom 
                sx={{ 
                  mb: isMobile ? 2 : 3,
                  color: '#FFA500',
                  fontWeight: 700
                }}
              >
                Platform Fee Wallet
              </Typography>
              <Box sx={{ 
                p: 2, 
                borderRadius: 2, 
                bgcolor: 'rgba(255, 165, 0, 0.1)', 
                border: '1px solid rgba(255, 165, 0, 0.3)' 
              }}>
                <Typography variant="body2" color="#B3B3B3" sx={{ mb: 1 }}>
                  Network: {networkConfig?.name || 'Unknown'}
                </Typography>
                <Typography variant="body2" color="#B3B3B3" sx={{ mb: 1, wordBreak: 'break-all' }}>
                  Platform Wallet: {getPlatformFeeWallet(currentNetwork)}
                </Typography>
                <Typography variant="body2" color="#B3B3B3" sx={{ mb: 1, wordBreak: 'break-all' }}>
                  Developer Wallet: 0x512D422791eed5626bB57E07f41c467950f82910
                </Typography>
                <Typography variant="body2" color="#FFA500" sx={{ fontWeight: 600 }}>
                  Fee Structure: 15% Platform + 4% Developer = 19% Total Fee
                </Typography>
              </Box>
            </GradientPaper>

            {/* Transfer Section */}
            <GradientPaper>
              <Typography 
                variant={isMobile ? 'body1' : 'h6'} 
                gutterBottom 
                sx={{ 
                  mb: isMobile ? 2 : 3,
                  color: '#0071EB',
                  fontWeight: 700
                }}
              >
                Transfer Fees
              </Typography>
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  label="Recipient Address"
                  value={transferAddress}
                  onChange={(e) => setTransferAddress(e.target.value)}
                  placeholder="0x..."
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '& fieldset': {
                        borderColor: 'rgba(255,255,255,0.2)'
                      }
                    }
                  }}
                />
                <TextField
                  fullWidth
                  label="Amount"
                  value={transferAmount}
                  onChange={(e) => setTransferAmount(e.target.value)}
                  placeholder="0.0"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '& fieldset': {
                        borderColor: 'rgba(255,255,255,0.2)'
                      }
                    }
                  }}
                />
              </Stack>
            </GradientPaper>

            {/* Fee Balances */}
            <GradientPaper>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                mb: 3
              }}>
                <Typography 
                  variant={isMobile ? 'body1' : 'h6'} 
                  sx={{ 
                    color: '#0071EB',
                    fontWeight: 700
                  }}
                >
                  Collected Fees
                </Typography>
                <Button
                  onClick={fetchFeeBalances}
                  disabled={loading}
                  startIcon={<RefreshIcon />}
                  size="small"
                  sx={{ color: '#0071EB' }}
                >
                  Refresh
                </Button>
              </Box>

              {loading ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <CircularProgress size={32} sx={{ color: '#0071EB', mb: 2 }} />
                  <Typography color="#B3B3B3">
                    Loading fee balances...
                  </Typography>
                </Box>
              ) : feeBalances.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="#B3B3B3">
                    No collected fees found for this network.
                  </Typography>
                </Box>
              ) : (
                <Grid container spacing={2}>
                  {feeBalances.map((token) => (
                    <Grid item xs={12} sm={6} md={4} key={token.address}>
                      <Card sx={{ 
                        background: 'linear-gradient(135deg, #141414 0%, rgba(20, 20, 20, 0.9) 100%)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: 2
                      }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6" sx={{ color: '#0071EB', fontWeight: 700 }}>
                              {token.symbol}
                            </Typography>
                            <Chip
                              label="Available"
                              color="success"
                              size="small"
                              icon={<CheckCircleIcon />}
                            />
                          </Box>
                          
                          <Typography variant="body2" color="#B3B3B3" sx={{ mb: 2 }}>
                            Balance: {token.formattedBalance} {token.symbol}
                          </Typography>
                          
                          <Button
                            fullWidth
                            variant="contained"
                            onClick={() => transferFees(token.address, token.formattedBalance)}
                            disabled={transferring[token.address] || !transferAddress}
                            startIcon={transferring[token.address] ? <CircularProgress size={16} /> : <SendIcon />}
                            sx={{
                              background: 'linear-gradient(135deg, #FFA500 0%, #FF8C00 100%)',
                              color: '#FFFFFF',
                              fontWeight: 600,
                              '&:hover': {
                                background: 'linear-gradient(135deg, #FF8C00 0%, #FF7F00 100%)',
                              }
                            }}
                          >
                            {transferring[token.address] ? 'Transferring...' : 'Transfer All'}
                          </Button>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </GradientPaper>
          </Stack>
        </Container>
      </Box>
    </ThemeProvider>
  );
}
